
import React, { useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import { formatCurrencyCOP } from '../utils/formatters';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { getPaymentBadgeClass } from '../utils/payments';

const PendingPaymentsOverview = ({ orders = [], onOpenPaymentModal, onOpenHistory, cashSessionOpen = true }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
    // 1. Títulos conocidos según captura del usuario
    const METHOD_CONFIG = {
        'recoge_bodega': { title: 'Recoge Bodega - Cliente Físico', icon: Icons.Store, color: 'blue' },
        'recoge_posterior': { title: 'Cliente WhatsApp Recoge Bodega', icon: Icons.MessageCircle, color: 'green' },
        'domicilio': { title: 'Domicilio local', icon: Icons.Bike, color: 'orange' },
        'nacional': { title: 'Nacional', icon: Icons.Truck, color: 'purple' },
        'mensajeria_urbana': { title: 'Mensajería Externa', icon: Icons.Package, color: 'teal' }
    };

    // 2. Filtrado por búsqueda Y forma de pago
    const filteredOrders = useMemo(() => {
        let result = orders;

        // Filtrar por búsqueda y excluir gestion_especial y cancelado
        result = result.filter(order => order.status !== 'gestion_especial' && order.status !== 'cancelado');

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            result = result.filter(order => {
                const orderNumber = (order.order_number || '').toLowerCase();
                const customerName = (order.customer_name || '').toLowerCase();
                return orderNumber.includes(term) || customerName.includes(term);
            });
        }

        // Filtrar por forma de pago
        if (paymentMethodFilter !== 'all') {
            result = result.filter(order => {
                const pm = (order.payment_method || '').toLowerCase();

                if (paymentMethodFilter === 'efectivo') {
                    return pm.includes('efectivo') || pm.includes('contado') || pm === 'cash';
                }
                if (paymentMethodFilter === 'transferencia') {
                    return pm.includes('transfer');
                }
                if (paymentMethodFilter === 'credito') {
                    return pm.includes('credito');
                }
                if (paymentMethodFilter === 'electronico') {
                    return pm.includes('pago') || pm.includes('tarjeta') || pm.includes('pse') || pm.includes('electron');
                }
                return true;
            });
        }

        return result;
    }, [orders, searchTerm, paymentMethodFilter]);

    // 3. Agrupación dinámica
    const groupings = useMemo(() => {
        const groups = {};

        filteredOrders.forEach(order => {
            const pending = order.calculated_total_debt !== undefined
                ? order.calculated_total_debt
                : (parseFloat(order.total_amount || 0) - parseFloat(order.paid_amount || 0));
            // Filter: Must have pending balance > 2000 (ignore rounding differences)
            if (pending <= 2000) return;

            // Normalizar key: si es nulo o vacío, usar 'sin_metodo'
            const methodKey = (order.delivery_method || 'sin_metodo').toLowerCase().trim();

            if (!groups[methodKey]) {
                const config = METHOD_CONFIG[methodKey] || {
                    title: methodKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    icon: Icons.Box,
                    color: 'gray'
                };

                groups[methodKey] = {
                    id: methodKey,
                    ...config,
                    orders: [],
                    total: 0
                };
            }

            groups[methodKey].orders.push(order);
            groups[methodKey].total += pending;
        });

        // Definir orden de prioridad
        const paymentMethodOrder = {
            'efectivo': 1,
            'transferencia': 2,
            'cliente_credito': 3,
            'contraentrega': 4,
            'datáfono': 5
        };

        const statusOrder = {
            'pendiente': 1,
            'pendiente_por_facturacion': 2,
            'revision_cartera': 3,
            'listo_para_entrega': 4,
            'listo_para_recoger': 5,
            'en_preparacion': 6,
            'en_logistica': 7,
            'preparado': 8,
            'en_ruta': 9,
            'entregado': 10
        };

        // Ordenar pedidos dentro de cada grupo: fecha más antigua primero, más reciente de última
        Object.keys(groups).forEach(key => {
            groups[key].orders.sort((a, b) => {
                const dateA = new Date(a.created_at || 0).getTime();
                const dateB = new Date(b.created_at || 0).getTime();
                return dateA - dateB; // Ascending: oldest first, newest last
            });
        });

        // Ordenar: primero los configurados en el orden de METHOD_CONFIG, luego los nuevos alfabéticamente
        const knownKeys = Object.keys(METHOD_CONFIG).filter(k => groups[k]);
        const otherKeys = Object.keys(groups).filter(k => !METHOD_CONFIG[k]).sort();

        return [...knownKeys, ...otherKeys].map(key => groups[key]);
    }, [filteredOrders]);

    // State for expanded sections (default all true)
    const [expandedSections, setExpandedSections] = useState(() => {
        return Object.keys(METHOD_CONFIG).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    });

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const getStatusColor = (status) => {
        const colors = {
            'pendiente': 'bg-yellow-100 text-yellow-800',
            'en_preparacion': 'bg-blue-100 text-blue-800',
            'preparado': 'bg-indigo-100 text-indigo-800',
            'en_ruta': 'bg-purple-100 text-purple-800',
            'entregado': 'bg-green-100 text-green-800',
            'listo_para_entrega': 'bg-blue-50 text-blue-700',
            'listo_para_recoger': 'bg-teal-50 text-teal-700'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const grandTotal = groupings.reduce((sum, g) => sum + g.total, 0);

    return (
        <div className="mb-8 space-y-6 mt-8">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 uppercase">
                    <Icons.Wallet className="w-6 h-6 text-gray-700" />
                    Todos los pedidos pendientes por pagar
                </h2>
                <div className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-sm">
                    <span className="text-xs uppercase tracking-wider opacity-75 mr-2">Total General</span>
                    <span className="text-xl font-bold">{formatCurrencyCOP(grandTotal)}</span>
                </div>
            </div>

            {/* Search + Payment Method Filters */}
            <div className="flex items-center gap-3">
                {/* Search - más pequeño */}
                <div className="flex items-center gap-2 bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm w-64">
                    <Icons.Search className="w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 outline-none text-sm"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <Icons.X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Payment Method Filter Buttons - using exact badge colors */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPaymentMethodFilter('all')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all bg-gray-200 text-gray-800 hover:bg-gray-300 ${paymentMethodFilter === 'all' ? 'ring-2 ring-offset-1 ring-gray-400 shadow-md' : 'shadow-sm'
                            }`}
                    >
                        TODOS
                    </button>
                    <button
                        onClick={() => setPaymentMethodFilter('efectivo')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${getPaymentBadgeClass('efectivo')} hover:bg-green-200 ${paymentMethodFilter === 'efectivo' ? 'ring-2 ring-offset-1 ring-green-500 shadow-md' : 'shadow-sm'
                            }`}
                    >
                        Efectivo
                    </button>
                    <button
                        onClick={() => setPaymentMethodFilter('transferencia')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${getPaymentBadgeClass('transferencia')} hover:bg-blue-200 ${paymentMethodFilter === 'transferencia' ? 'ring-2 ring-offset-1 ring-blue-500 shadow-md' : 'shadow-sm'
                            }`}
                    >
                        Transferencia
                    </button>
                    <button
                        onClick={() => setPaymentMethodFilter('credito')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${getPaymentBadgeClass('cliente_credito')} hover:bg-yellow-200 ${paymentMethodFilter === 'credito' ? 'ring-2 ring-offset-1 ring-yellow-500 shadow-md' : 'shadow-sm'
                            }`}
                    >
                        Cliente Crédito
                    </button>
                    <button
                        onClick={() => setPaymentMethodFilter('electronico')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${getPaymentBadgeClass('pago_electronico')} hover:bg-indigo-200 ${paymentMethodFilter === 'electronico' ? 'ring-2 ring-offset-1 ring-indigo-500 shadow-md' : 'shadow-sm'
                            }`}
                    >
                        Pago Electrónico
                    </button>
                </div>
            </div>

            {/* Empty State when search finds nothing */}
            {groupings.length === 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Icons.Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">
                        {searchTerm
                            ? `No se encontraron pedidos que coincidan con "${searchTerm}"`
                            : 'No hay pedidos pendientes por pagar'}
                    </p>
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                            Limpiar búsqueda
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 gap-6">
                {groupings.map((group) => {
                    const isExpanded = expandedSections[group.id] !== false;

                    const colorClasses = {
                        blue: 'bg-blue-50 border-blue-200 text-blue-800',
                        green: 'bg-green-50 border-green-200 text-green-800',
                        orange: 'bg-orange-50 border-orange-200 text-orange-800',
                        purple: 'bg-purple-50 border-purple-200 text-purple-800',
                        teal: 'bg-teal-50 border-teal-200 text-teal-800',
                        gray: 'bg-gray-50 border-gray-200 text-gray-800'
                    };

                    const iconColor = {
                        blue: 'text-blue-600',
                        green: 'text-green-600',
                        orange: 'text-orange-600',
                        purple: 'text-purple-600',
                        teal: 'text-teal-600',
                        gray: 'text-gray-600'
                    };

                    const activeBg = colorClasses[group.color] || colorClasses['gray'];
                    const activeIcon = iconColor[group.color] || iconColor['gray'];

                    return (
                        <div key={group.id} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${isExpanded ? 'ring-1 ring-opacity-50' : ''} ${isExpanded ? `ring-${group.color}-400` : 'border-gray-200'}`}>
                            {/* Header */}
                            <div
                                className={`px-4 py-3 cursor-pointer flex justify-between items-center transition-colors ${isExpanded ? activeBg : 'bg-gray-50 hover:bg-gray-100'}`}
                                onClick={() => toggleSection(group.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <group.icon className={`w-5 h-5 ${activeIcon}`} />
                                    <span className="font-bold text-sm md:text-base truncate max-w-[300px]" title={group.title}>{group.title}</span>
                                    <span className="px-2 py-0.5 bg-white bg-opacity-60 rounded-full text-xs font-bold">
                                        {group.orders.length}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-bold">{formatCurrencyCOP(group.total)}</span>
                                    {isExpanded ? <Icons.ChevronUp className="w-4 h-4" /> : <Icons.ChevronDown className="w-4 h-4" />}
                                </div>
                            </div>

                            {/* Content */}
                            {isExpanded && (
                                <div className="max-h-96 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase tracking-wider">
                                            <tr>
                                                <th className="px-6 py-3 text-left font-medium text-gray-500">Pedido</th>
                                                <th className="px-6 py-3 text-left font-medium text-gray-500">Cliente</th>
                                                <th className="px-6 py-3 text-center font-medium text-gray-500">Forma de Entrega</th>
                                                <th className="px-6 py-3 text-center font-medium text-gray-500">Forma de Pago</th>
                                                <th className="px-6 py-3 text-center font-medium text-gray-500">Estado</th>
                                                <th className="px-6 py-3 text-right font-medium text-gray-500">Monto</th>
                                                <th className="px-6 py-3 text-center font-medium text-gray-500">Gestión</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {group.orders.map((order) => {
                                                const pending = order.calculated_total_debt !== undefined
                                                    ? order.calculated_total_debt
                                                    : (parseFloat(order.total_amount || 0) - parseFloat(order.paid_amount || 0));
                                                const dateStr = order.created_at ? format(new Date(order.created_at), "d MMM, HH:mm", { locale: es }) : '-';

                                                return (
                                                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                                        {/* PEDIDO */}
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-bold text-gray-900">#{order.order_number}</div>
                                                            <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                                                                <Icons.Calendar className="w-3 h-3" />
                                                                {dateStr}
                                                            </div>
                                                        </td>

                                                        {/* CLIENTE */}
                                                        <td className="px-6 py-4">
                                                            <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                                                            <div className="text-xs text-gray-500">{order.customer_phone}</div>
                                                        </td>

                                                        {/* FORMA DE ENTREGA */}
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                                                {(order.delivery_method || 'N/A').replace(/_/g, ' ')}
                                                            </span>
                                                        </td>

                                                        {/* FORMA DE PAGO */}
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="text-xs font-bold text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-200 uppercase tracking-wide">
                                                                {order.payment_method || 'N/A'}
                                                            </span>
                                                        </td>

                                                        {/* ESTADO */}
                                                        <td className="px-6 py-4 text-center">
                                                            <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                                                {(order.status || '').replace(/_/g, ' ')}
                                                            </span>
                                                        </td>

                                                        {/* MONTO */}
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="text-sm font-bold text-gray-900">{formatCurrencyCOP(order.total_amount)}</div>
                                                            <div className="text-xs font-bold text-red-500 mt-0.5">Debe: {formatCurrencyCOP(pending)}</div>
                                                        </td>

                                                        {/* GESTIÓN */}
                                                        <td className="px-6 py-4 text-center">
                                                            <div className="flex items-center justify-center gap-2">
                                                                {/* Botón de pago */}
                                                                <button
                                                                    className={`p-2 rounded-full transition-all shadow-sm hover:shadow-md ${cashSessionOpen ? 'bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (onOpenPaymentModal && cashSessionOpen) {
                                                                            onOpenPaymentModal(order);
                                                                        }
                                                                    }}
                                                                    disabled={!cashSessionOpen}
                                                                    title={cashSessionOpen ? 'Aplicar pago' : 'Abra una caja para aplicar pagos'}
                                                                >
                                                                    <Icons.DollarSign className="w-5 h-5" />
                                                                </button>

                                                                {/* Botón Factura SIIGO */}
                                                                {order.siigo_public_url && (
                                                                    <a
                                                                        href={order.siigo_public_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-purple-600 hover:text-purple-900 transition-colors"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        title="Ver factura SIIGO"
                                                                    >
                                                                        <Icons.FileText className="w-5 h-5" />
                                                                    </a>
                                                                )}

                                                                {/* Botón de historial */}
                                                                <button
                                                                    className="text-gray-400 hover:text-indigo-600 transition-colors"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (onOpenHistory) {
                                                                            onOpenHistory(order);
                                                                        }
                                                                    }}
                                                                    title="Ver historial"
                                                                >
                                                                    <Icons.History className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PendingPaymentsOverview;
